'use client';

import { useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth, useProcessMyUser } from './store/auth';
import { useConnection } from './store/connection';
import { api, recordServerTime, getLastServerTime, serverNow } from './api';
import type { SessionMessage, LinkedMessage, Session, Avatar, Link, User } from './api';
import { useProcessMyAvatar, useProcessMyAvatarList, processAvatar } from './queries/useAvatars';
import { useProcessMySession, useProcessMySessionList } from './queries/useSessions';
import { useProcessMyLink, useProcessMyLinkList } from './queries/useLinks';

// Above this gap between the captured watermark and the reconnect, we
// don't trust `?updatedAfter=` anymore — the response could be huge and
// we'd render N rows through the cache writers when a single fresh
// fetch is cheaper. Invalidate the inbox families instead: TanStack
// triggers a refetch on every mounted observer (foreground page) and
// the rest cold-fetch on next mount.
const STALE_RECONNECT_MS = 5 * 60 * 1000;

// Socket.io semantics: when the first arg starts with `/`, it's the
// **namespace** (not a URL path). The HTTP path where engine.io listens
// is set via `path`, and it's the prefix that nginx strips off in front
// of `/socket.io/?…` requests. Our nginx maps `/ws/` →
// `127.0.0.1:3000/socket.io/`, so the client must use `path: '/ws/'`
// and the namespace must match the gateway's `/ws`.
const WS_NAMESPACE = '/ws';
const WS_PATH      = process.env.NEXT_PUBLIC_WS_URL ?? '/ws/';

// Module-level singleton for code outside the layout that needs to
// emit on the live socket (e.g. useChatPresence). Mirrors the ref
// inside `useSocket` — set after creation, cleared on cleanup.
let activeSocket: Socket | null = null;
export function getActiveSocket(): Socket | null { return activeSocket; }

export function useSocket() {
  const socketRef = useRef<Socket | null>(null);
  const qc = useQueryClient();
  // Subscribe to the id only — auth identity. Other fields on the user
  // object (countLinks, photoUrl, …) get bumped on every reloadYourUser,
  // and we don't want to tear down the socket each time. Pulling the
  // whole object into deps would re-run this effect on every profile
  // mutation, racing with the very events that triggered the mutation.
  const userId = useAuth((s) => s.user?.id);
  const handleMyAvatar = useProcessMyAvatar();
  const handleMyAvatarList = useProcessMyAvatarList();
  const handleMySession = useProcessMySession();
  const handleMySessionList = useProcessMySessionList();
  const handleMyLink = useProcessMyLink();
  const handleMyLinkList = useProcessMyLinkList();
  const handleMyUser = useProcessMyUser();

  useEffect(() => {
    if (!userId) return;

    // Auth via the splace_session cookie — withCredentials makes Socket.io
    // include it on the upgrade request, the gateway pulls the JWT from there.
    const socket = io(WS_NAMESPACE, {
      path: WS_PATH,
      withCredentials: true,
      transports: ['websocket'],
      // Defaults already retry forever with backoff, but be explicit so the
      // intent is documented in the codebase.
      reconnection: true,
      reconnectionDelay: 500,
      reconnectionDelayMax: 5_000,
    });
    socketRef.current = socket;
    activeSocket = socket;

    // Every WS event carries `time` (DB clock) — keep serverNow() calibrated
    // even when the user is idle in a chat with no HTTP requests in flight.
    socket.onAny((_event, data) => {
      if (data && typeof data.time === 'number') recordServerTime(data.time);
    });

    // Steady-state strategy: initial HTTP fetch warms each list, socket
    // events keep them current. On reconnect, the per-page lastReconnectAt
    // watcher pulls a delta of the resources the user is actively viewing
    // (messages tail). Inbox-list catch-up uses `?updatedAfter=<watermark>`
    // against the four BE delta endpoints in parallel: seeker sessions,
    // avatar sessions, links, avatar history (memories).
    //
    // The watermark is snapshotted on `disconnect` / browser `offline` and
    // used on the next `connect`. Reading lastServerTime *at connect time*
    // is wrong: between the browser `online` event and the socket reconnect,
    // TanStack Query auto-refetches stale queries — those responses bump
    // lastServerTime past the offline window, and the catch-up
    // `?updatedAfter=<now>` returns nothing (skipping every event that
    // happened during the disconnect).
    let syncedUntil = 0;
    const { setStatus, bumpReconnect, setSocketId } = useConnection.getState();
    socket.on('connect', () => {
      const watermark = syncedUntil;
      setStatus('online');
      bumpReconnect();
      setSocketId(socket.id ?? null);

      // First connect of the lifecycle (no prior offline window) — skip
      // the delta call, the page-level useQuery does the cold fetch.
      if (watermark > 0) {
        // /users/me has no delta param (single row); always refetch
        // so anything missed during the offline window (countLinks,
        // photoUrl, …) lands in the auth store on every reconnect.
        api.users.me().then(handleMyUser).catch(() => { /* next reconnect retries */ });

        const offlineMs = serverNow() - watermark;
        if (offlineMs > STALE_RECONNECT_MS) {
          // Too long offline — invalidate the four inbox families.
          // The page in foreground refetches now; the others on next
          // navigate. Cheaper than a delta that could return hundreds
          // of rows funneled through cache writers.
          qc.invalidateQueries({ queryKey: ['sessions', 'mine'] });
          qc.invalidateQueries({ queryKey: ['links', 'mine'] });
          qc.invalidateQueries({ queryKey: ['avatar', 'history'] });
        } else {
          Promise.all([
            api.sessions.seekerMine({ updatedAfter: watermark }),
            api.sessions.avatarMine({ updatedAfter: watermark }),
            api.links.mine({ updatedAfter: watermark }),
            api.avatars.history({ updatedAfter: watermark }),
          ]).then(([seeker, avatar, links, history]) => {
            // Route each response through its batched writer so each
            // inbox cache re-renders once instead of N times.
            handleMySessionList([...seeker.items, ...avatar.items]);
            handleMyLinkList(links.items);
            handleMyAvatarList(history.items);
          }).catch(() => { /* swallow — next reconnect retries */ });
        }
      }
      // We're back in sync; the next disconnect re-captures from here.
      syncedUntil = 0;
    });
    socket.on('disconnect', () => {
      setStatus('reconnecting');
      setSocketId(null);
      // Freeze the floor for the next reconnect. Subsequent HTTP refetches
      // during the offline-then-online transition won't shift it.
      if (syncedUntil === 0) syncedUntil = getLastServerTime();
    });
    socket.io.on('reconnect_attempt', () => setStatus('reconnecting'));
    socket.io.on('error', () => setStatus('reconnecting'));

    // Browser-level offline detection — covers cases where the socket
    // doesn't immediately notice (e.g. system goes to sleep). When the
    // browser comes back, the socket reconnect kicks in and the
    // `connect` listener flips us back to online.
    const onOffline = () => {
      setStatus('offline');
      // Browser knew before the socket — capture the watermark NOW so
      // any TanStack refetch fired by the upcoming `online` event can't
      // race ahead of the catch-up floor.
      if (syncedUntil === 0) syncedUntil = getLastServerTime();
    };
    // iOS Safari sometimes fires `online` *after* socket.io has already
    // reconnected and flipped status to 'online'. Without this guard
    // `onOnline` overwrites the live state with 'reconnecting' and the
    // UI gets stuck on the pulse forever. Only flag reconnecting if
    // the socket really isn't connected yet.
    const onOnline = () => { if (!socket.connected) setStatus('reconnecting'); };
    window.addEventListener('offline', onOffline);
    window.addEventListener('online',  onOnline);

    // ── Chat ───────────────────────────────────────────────────────────
    // The `newMessage` payload now carries the freshly-decorated
    // session/link row, so we route through processMySession /
    // processMyLink (refreshes inbox card + per-id cache) instead of
    // invalidating and refetching. Falls back to invalidate when a
    // payload arrives without the row (defensive during a rolling
    // deploy).
    socket.on('newMessage', ({ sessionId, linkId, message, session, link }: {
      sessionId?: string;
      linkId?: string;
      message?: SessionMessage | LinkedMessage;
      session?: Session;
      link?: Link;
    }) => {
      if (sessionId && session) {
        handleMySession(session);
      } else if (sessionId) {
        qc.invalidateQueries({ queryKey: ['sessions', 'mine'] });
      }
      if (linkId && link) {
        handleMyLink(link);
      } else if (linkId) {
        qc.invalidateQueries({ queryKey: ['links', 'mine'] });
      }

      if (sessionId && message) {
        type MsgPage = { session: SessionMessage; messages: SessionMessage[] };
        qc.setQueryData<{ pages: MsgPage[]; pageParams: unknown[] }>(
          ['sessions', sessionId, 'messages'],
          (old) => {
            if (!old?.pages.length) return old;
            const pages = old.pages.slice();
            const last = pages[pages.length - 1];
            pages[pages.length - 1] = { ...last, messages: [...last.messages, message as SessionMessage] };
            return { ...old, pages };
          },
        );
      } else if (sessionId) {
        qc.invalidateQueries({ queryKey: ['sessions', sessionId, 'messages'] });
      }

      if (linkId && message) {
        type LinkMsgPage = { messages: LinkedMessage[]; next: number | null; prev: number | null };
        qc.setQueryData<{ pages: LinkMsgPage[]; pageParams: unknown[] }>(
          ['links', linkId, 'messages'],
          (old) => {
            if (!old?.pages.length) return old;
            const pages = old.pages.slice();
            const last = pages[pages.length - 1];
            pages[pages.length - 1] = { ...last, messages: [...last.messages, message as LinkedMessage] };
            return { ...old, pages };
          },
        );
      } else if (linkId) {
        qc.invalidateQueries({ queryKey: ['links', linkId, 'messages'] });
      }
    });

    // ── Links ──────────────────────────────────────────────────────────
    // Single event for every link state change driven by the BE.
    // The link is decorated per receiver: `cutByMe` flags the
    // originator's other tabs (drop the row), `cutByPartner` flags
    // the partner (read-only). processMyLink branches on cutByMe.
    socket.on('reloadYourLink', ({ link }: { link: Link }) => {
      handleMyLink(link);
      // The /chat/<avatarId> preview reads the link state from
      // previewChat (`linked` hint). The socket payload IS the truth —
      // we never refetch. On a cut (either side), walk every cached
      // preview and clear `linked` where linkId matches; the banner
      // disappears, the composer comes back. Re-link is a no-op here:
      // the preview cache doesn't know which avatarId belongs to which
      // userId (avatar.userId is stripped for anonymity), and the
      // re-link flow naturally lives on /session or /link, not /chat.
      type PreviewCache = {
        avatar: unknown;
        session: unknown;
        linked: { linkId: string; linkedAt: number } | null;
      };
      if (link.cutByMe || link.cutByPartner) {
        qc.setQueriesData<PreviewCache>({ queryKey: ['sessions', 'open'] }, (old) => {
          if (old?.linked?.linkId === link.id) return { ...old, linked: null };
          return old;
        });
      }
    });

    // ── Session state ─────────────────────────────────────────────
    // Per-user channel: BE emits `reloadYourSession` with the full row
    // to both participants on every session lifecycle change (create,
    // delete, restore). Originating tab is excluded server-side via
    // X-Socket-Id. processMySession is the sole writer of
    // ['sessions','mine'] outside the cold/reconnect fetch.
    socket.on('reloadYourSession', ({ session }: { session: Session }) => {
      handleMySession(session);
    });

    // ── Avatar lifecycle ──────────────────────────────────────────────
    // Owner channel: BE pushes the full avatar after every explicit
    // *non-end* state change (create / photo / deactivate-of-other
    // ops / delete). Originating tab is excluded server-side via
    // X-Socket-Id, so this fires on the user's *other* devices.
    // Explicit `endAvatar` and natural expiry don't go through here:
    //   - endAvatar uses the `reloadAvatar` broadcast below, which
    //     covers feed + own-avatar + sessions in one shot.
    //   - natural expiry stays silent (cron emits nothing) — the
    //     global tick reaper detects it locally on each device.
    socket.on('reloadYourAvatar', ({ avatar }: { avatar: Avatar }) => {
      handleMyAvatar(avatar);
    });

    // Broadcast emitted on explicit end. Carries the avatar's lifecycle
    // stamps so every receiver can update its caches locally without a
    // refetch. processAvatar smistas: feed drop, own-avatar route via
    // processMyAvatar, session caches stamped per matching avatarId.
    socket.on('reloadAvatar', (payload: { avatarId: string; expiredAt: number; endedAt: number | null }) => {
      processAvatar(qc, payload);
    });

    // ── My own user ──────────────────────────────────────────────────
    // BE emits this whenever a denormalised field on the caller changes
    // (today: countLinks bumped on accept / cut). Originating tab is
    // excluded via X-Socket-Id; the partner's tabs and the cutter's
    // other tabs all catch up here without polling /users/me.
    socket.on('reloadYourUser', ({ user }: { user: User }) => {
      handleMyUser(user);
    });

    return () => {
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online',  onOnline);
      socket.disconnect();
      socketRef.current = null;
      activeSocket = null;
    };
  }, [userId, qc, handleMyAvatar, handleMyAvatarList, handleMySession, handleMySessionList, handleMyLink, handleMyLinkList, handleMyUser]);

  return socketRef.current;
}
